import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import API from "@/lib/api";
import { DialogBody } from "@/components/shared/DialogBody";
import { MODE_FILTERS, CATEGORY_TAGS } from "@/lib/libraryConstants";

/**
 * Self-contained Suggest Additions dialog. AI analyzes library and suggests
 * new blocks to add for gap coverage.
 */
export function SuggestDialog({ open, onOpenChange, onSuccess, initialMode = "", initialCategory = "", embedded = false }) {
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [savingSuggestion, setSavingSuggestion] = useState(null);
  const [suggestMode, setSuggestMode] = useState(initialMode);
  const [suggestCategory, setSuggestCategory] = useState(initialCategory);

  // Re-seed + auto-run when the dialog re-opens with new initial scope (e.g.,
  // user clicked a different Coverage-Matrix cell).
  useEffect(() => {
    if (!open) return;
    setSuggestMode(initialMode);
    setSuggestCategory(initialCategory);
    if (initialMode || initialCategory) {
      // Fire the suggestion immediately with the pre-scoped values
      handleSuggest(initialMode, initialCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMode, initialCategory]);

  const handleSuggest = async (mode, category) => {
    const m = mode !== undefined ? mode : suggestMode;
    const c = category !== undefined ? category : suggestCategory;
    setSuggesting(true);
    setSuggestions(null);
    try {
      const { data } = await API.post("/library/suggest", { mode: m || "", category: c || "" });
      setSuggestions(data);
    } catch (e) {
      toast.error("Suggestion failed", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setSuggesting(false);
    }
  };

  const handleAccept = async (suggestion, idx) => {
    setSavingSuggestion(idx);
    try {
      await API.post("/library/import/save", { blocks: [{ ...suggestion, build_type: "general" }] });
      toast.success(`Added "${suggestion.title}" to library`);
      setSuggestions((prev) => ({ ...prev, suggestions: prev.suggestions.filter((_, i) => i !== idx) }));
      onSuccess?.();
    } catch (e) {
      toast.error("Failed to add");
    } finally {
      setSavingSuggestion(null);
    }
  };

  const handleAcceptAll = async () => {
    const allSuggestions = suggestions?.suggestions || [];
    if (allSuggestions.length === 0) return;
    setSavingSuggestion("all");
    try {
      await API.post("/library/import/save", { blocks: allSuggestions.map((s) => ({ ...s, build_type: "general" })) });
      toast.success(`Added ${allSuggestions.length} blocks to library`);
      setSuggestions((prev) => ({ ...prev, suggestions: [] }));
      onSuccess?.();
    } catch (e) {
      toast.error("Failed to add suggestions");
    } finally {
      setSavingSuggestion(null);
    }
  };

  const handleClose = (next) => {
    onOpenChange(next);
    if (!next) { setSuggestions(null); setSuggestMode(""); setSuggestCategory(""); }
  };

  const body = (
    <>
      {!embedded && (
        <DialogHeader>
          <DialogTitle className="font-heading text-lg font-bold">Suggest Additions</DialogTitle>
        </DialogHeader>
      )}

      <div className="space-y-3" data-testid="suggest-mode-selector">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] mb-1.5">Mode</p>
            <p className="text-xs text-[var(--pf-text-secondary)] mb-2">Focus suggestions on a specific development mode.</p>
            <div className="flex flex-wrap gap-1.5">
              {[{ value: "", label: "All Modes" }, ...MODE_FILTERS.filter((m) => m !== "all").map((m) => ({ value: m, label: m.charAt(0).toUpperCase() + m.slice(1) }))].map((opt) => (
                <button key={opt.value} onClick={() => setSuggestMode(opt.value)} data-testid={`suggest-mode-${opt.value || "all"}`}
                  className={`text-[11px] px-2.5 py-1.5 font-medium transition-colors ${suggestMode === opt.value ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] hover:bg-[var(--pf-accent)] hover:text-white"}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div data-testid="suggest-category-selector">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] mb-1.5">Category</p>
            <p className="text-xs text-[var(--pf-text-secondary)] mb-2">Narrow suggestions to a specific area (e.g., UX, Auth, Navigation).</p>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setSuggestCategory("")} data-testid="suggest-category-all"
                className={`text-[11px] px-2.5 py-1.5 font-medium transition-colors ${!suggestCategory ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] hover:bg-[var(--pf-accent)] hover:text-white"}`}>
                Any Category
              </button>
              {CATEGORY_TAGS.map((cat) => (
                <button key={cat} onClick={() => setSuggestCategory(suggestCategory === cat ? "" : cat)} data-testid={`suggest-category-${cat.toLowerCase().replace(/\s/g, "-")}`}
                  className={`text-[11px] px-2.5 py-1.5 font-medium transition-colors ${suggestCategory === cat ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] hover:bg-[var(--pf-accent)] hover:text-white"}`}>
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {!suggestions && !suggesting && (
            <Button onClick={() => handleSuggest(suggestMode)} data-testid="suggest-generate-button"
              className="w-full rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-9 text-xs font-medium mt-1">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />Generate Suggestions{suggestMode ? ` for ${suggestMode.charAt(0).toUpperCase() + suggestMode.slice(1)}` : ""}{suggestCategory ? ` — ${suggestCategory}` : ""}
            </Button>
          )}
        </div>

        {suggesting ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--pf-accent)] mx-auto mb-3" />
            <p className="text-sm text-[var(--pf-text-secondary)]">Analyzing your library{suggestMode ? ` for ${suggestMode} gaps` : ""}{suggestCategory ? ` in ${suggestCategory}` : ""}...</p>
          </div>
        ) : suggestions ? (
          <div className="space-y-4" data-testid="suggest-results">
            {suggestions.analysis && (
              <div className="border-l-2 border-[var(--pf-accent)] bg-[var(--pf-surface)] px-3 py-2">
                <p className="text-xs text-[var(--pf-text-secondary)]">{suggestions.analysis}</p>
              </div>
            )}
            {(suggestions.suggestions || []).length === 0 ? (
              <p className="text-sm text-[var(--pf-text-muted)] text-center py-4">No gaps found{suggestMode ? ` in ${suggestMode} mode` : ""}{suggestCategory ? ` for ${suggestCategory}` : ""} — your library looks comprehensive.</p>
            ) : (
              <div className="space-y-3">
                {(suggestions.suggestions || []).length > 1 && (
                  <Button onClick={handleAcceptAll} disabled={savingSuggestion === "all"} data-testid="accept-all-suggestions"
                    className="w-full rounded-none bg-[#16A34A] hover:bg-[#15803d] text-white h-8 text-xs font-medium">
                    {savingSuggestion === "all" ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Adding all...</> : <>Add All {(suggestions.suggestions || []).length} to Library</>}
                  </Button>
                )}
                {(suggestions.suggestions || []).map((s, idx) => (
                  <div key={idx} className="border border-[var(--pf-border)] p-3" data-testid={`suggestion-${idx}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-[var(--pf-text)] uppercase tracking-[0.1em]">{s.title}</span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-[var(--pf-surface)] text-[var(--pf-text-muted)]">{s.mode}</span>
                      </div>
                      <Button size="sm" onClick={() => handleAccept(s, idx)} disabled={savingSuggestion === idx} data-testid={`accept-suggestion-${idx}`}
                        className="rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-7 px-2.5 text-[11px]">
                        {savingSuggestion === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add to Library"}
                      </Button>
                    </div>
                    <p className="text-xs text-[var(--pf-text)] font-mono whitespace-pre-wrap mb-1.5">{s.content}</p>
                    {s.reason && <p className="text-[10px] text-[var(--pf-text-muted)] italic">{s.reason}</p>}
                    <div className="flex items-center gap-1 mt-1">
                      {(s.tags || []).map((tag) => (<span key={tag} className="text-[10px] px-1.5 py-0.5 bg-[var(--pf-surface)] text-[var(--pf-text-muted)] border border-[var(--pf-border)]">{tag}</span>))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button variant="outline" onClick={() => handleSuggest(suggestMode)} disabled={suggesting} data-testid="suggest-refresh"
              className="w-full rounded-none border-[var(--pf-border)] h-8 text-xs hover:border-[var(--pf-accent)]">
              <Sparkles className="w-3 h-3 mr-1.5" />Regenerate{suggestMode ? ` for ${suggestMode.charAt(0).toUpperCase() + suggestMode.slice(1)}` : ""}{suggestCategory ? ` — ${suggestCategory}` : ""}
            </Button>
          </div>
        ) : null}
    </>
  );

  if (embedded) return body;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-none border-[var(--pf-border)] sm:max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogBody>{body}</DialogBody>
      </DialogContent>
    </Dialog>
  );
}
